import type { DataTableColumn } from "@etudescarre/ui";
import { Button, cn, Modal } from "@etudescarre/ui";
import { isNil, isNotNil } from "@etudescarre/utils";
import { FilePdf as FilePdfIcon } from "@phosphor-icons/react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useParams } from "react-router";
import { apiClient } from "@/lib/api/client";
import { formatDay } from "@/lib/utils/date";
import { Classeur } from "../components/classeur";
import { ProposalSendBar } from "../components/document-table/proposal-send-bar";
import { useOnProposalsChanged } from "../components/document-table/proposals-changed";
import { formatDateEtat } from "../components/reception-sas/maquettes-table";

// Écran 040, N180 : ce que le rang 2 a proposé, et ce qu'il en est advenu.
// La carte place ces états dans la liste 035 ; le bandeau dans la liste et ses
// boutons Modifier, Reformuler, Supprimer restent à faire.

type Proposal = {
  id: string;
  kind: "document" | "maquette";
  versionId: number;
  number: string | null;
  name: string | null;
  index: string | null;
  dateEtat: string | null;
  state: "draft" | "sent" | "refused";
  sentAt: string | null;
  rejectionReason: string | null;
  fichierPdfId: number | null;
};

const STATE_LABELS: Record<Proposal["state"], string> = {
  draft: "Brouillon",
  sent: "Proposé",
  refused: "Refusé",
};

const STATE_CLASSES: Record<Proposal["state"], string> = {
  draft: "bg-zinc-100 text-zinc-700 dark:bg-zinc-800 dark:text-zinc-300",
  sent: "bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300",
  refused: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300",
};

const VIEWS: { kind: Proposal["kind"]; label: string }[] = [
  { kind: "document", label: "Documents" },
  { kind: "maquette", label: "Maquettes" },
];

export function Propositions() {
  const { yardId = "" } = useParams<{ yardId: string }>();
  const [items, setItems] = useState<Proposal[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [view, setView] = useState<Proposal["kind"]>("document");
  const [refused, setRefused] = useState<Proposal | null>(null);

  const fetchProposals = useCallback(async () => {
    const { data } = await apiClient.GET("/api/yards/{yardId}/proposals", {
      params: { path: { yardId } },
    });
    setItems(
      data?.data.items.map((item) => ({
        ...item,
        id: `${item.kind}-${item.versionId}`,
      })) ?? [],
    );
    setIsLoading(false);
  }, [yardId]);

  useEffect(() => {
    fetchProposals();
  }, [fetchProposals]);
  useOnProposalsChanged(fetchProposals);

  const openPdf = useCallback(
    async (fichierId: number) => {
      const { data } = await apiClient.GET(
        "/api/yards/{yardId}/fichiers/{fichierId}/url",
        { params: { path: { yardId, fichierId: String(fichierId) } } },
      );
      if (isNotNil(data)) window.open(data.data.inlineUrl, "_blank");
    },
    [yardId],
  );

  const columns = useMemo<DataTableColumn<Proposal>[]>(
    () => [
      { id: "number", header: "N°", accessor: "number" },
      { id: "name", header: "Intitulé", accessor: "name" },
      view === "document"
        ? { id: "index", header: "Ind.", accessor: "index" }
        : {
            id: "dateEtat",
            header: "Date d'état",
            accessor: "dateEtat",
            cell: ({ row }) => formatDateEtat(row),
          },
      {
        id: "state",
        header: "État",
        accessor: "state",
        cell: ({ row }) =>
          row.state === "refused" ? (
            <button
              type="button"
              onClick={() => setRefused(row)}
              className={cn(
                "rounded px-2 py-0.5 text-xs font-medium underline",
                STATE_CLASSES.refused,
              )}
            >
              {STATE_LABELS.refused}
            </button>
          ) : (
            <span
              className={cn(
                "rounded px-2 py-0.5 text-xs font-medium",
                STATE_CLASSES[row.state],
              )}
            >
              {STATE_LABELS[row.state]}
            </span>
          ),
      },
      {
        id: "sentAt",
        header: "Date de dépôt",
        accessor: "sentAt",
        cell: ({ value }) => (value ? formatDay(value as string) : "-"),
      },
      {
        id: "pdf",
        header: "PDF",
        accessor: "fichierPdfId",
        cell: ({ row }) =>
          isNil(row.fichierPdfId) ? (
            <span className="text-zinc-300">-</span>
          ) : (
            <button
              type="button"
              aria-label={`Ouvrir le PDF de ${row.number}`}
              onClick={() => row.fichierPdfId && openPdf(row.fichierPdfId)}
            >
              <FilePdfIcon size={18} className="text-red-600" />
            </button>
          ),
      },
    ],
    [openPdf, view],
  );

  const shown = items.filter((item) => item.kind === view);

  return (
    <>
      <Classeur
        title="Mes propositions"
        items={shown}
        columns={columns}
        filters={[]}
        activeFilters={{}}
        onFilterChange={() => {}}
        isLoading={isLoading}
        emptyMessage="Aucune proposition"
        total={shown.length}
        actions={
          <div className="flex items-center gap-4">
            <div className="flex gap-1">
              {VIEWS.map(({ kind, label }) =>
                view === kind ? (
                  <Button key={kind} className="text-sm">
                    {label}
                  </Button>
                ) : (
                  <Button
                    key={kind}
                    outline
                    onClick={() => setView(kind)}
                    className="text-sm"
                  >
                    {label}
                  </Button>
                ),
              )}
            </div>
            <ProposalSendBar />
          </div>
        }
      />

      <Modal.Root
        open={isNotNil(refused)}
        onOpenChange={(e) => !e.open && setRefused(null)}
      >
        <Modal.Backdrop />
        <Modal.Positioner>
          <Modal.Content size="md">
            <Modal.Header>
              <Modal.Title>Motif du refus</Modal.Title>
            </Modal.Header>
            <div className="px-6 pb-6 text-sm text-zinc-700 dark:text-zinc-300">
              <p className="mb-2 font-medium">
                {refused?.number} {refused?.name}
              </p>
              <p>{refused?.rejectionReason ?? "Aucun motif renseigné"}</p>
              <div className="mt-6 flex justify-end">
                <Button outline onClick={() => setRefused(null)}>
                  Fermer
                </Button>
              </div>
            </div>
          </Modal.Content>
        </Modal.Positioner>
      </Modal.Root>
    </>
  );
}
